import { useMemo } from "react";

/* ------------------------------- helpers ------------------------------- */
function isValidHttpUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function clampStr(s, n) {
  return String(s || "").slice(0, n);
}

function cleanMoney(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return "INVALID";
  return s;
}

function uniq(arr) {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const k = String(x || "").trim();
    if (!k) continue;
    const key = k.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(k);
  }
  return out;
}

/* ------------------------------ ui bits ------------------------------ */
function Badge({ children, tone = "neutral" }) {
  const toneCls =
    tone === "sky"
      ? "border-sky-300/25 bg-sky-300/10 text-sky-200"
      : tone === "danger"
      ? "border-red-400/25 bg-red-400/10 text-red-200"
      : "border-white/10 bg-white/[0.04] text-white/60";

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold ${toneCls}`}
    >
      {children}
    </span>
  );
}

function MiniPill({ children, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1 text-xs font-semibold transition",
        active
          ? "border-sky-300/25 bg-sky-300/10 text-sky-200"
          : "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.07]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* ------------------------------ constants ------------------------------ */
export const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY"];

export const CATEGORY_PRESETS = [
  { key: "apparel", label: "Apparel" },
  { key: "prints", label: "Prints" },
  { key: "stickers", label: "Stickers" },
  { key: "commissions", label: "Commissions" },
  { key: "digital", label: "Digital" },
  { key: "other", label: "Other" },
];

export const PRODUCT_TYPE_SUGGESTIONS = {
  apparel: ["T-Shirt", "Hoodie", "Crewneck", "Jacket", "Hat"],
  prints: ["Poster", "Canvas", "Art Print", "Photo Print", "Framed Print"],
  stickers: ["Sticker Pack", "Vinyl Sticker", "Holographic Sticker"],
  commissions: ["Commission Slot", "Portrait", "Character"],
  digital: ["Wallpaper Pack", "Digital Download", "Brush Pack"],
  other: ["Store", "Portfolio", "Link"],
};

export const PLATFORM_SUGGESTIONS = [
  "Printify",
  "Etsy",
  "Shopify",
  "Gumroad",
  "Ko-fi",
  "Patreon",
  "InPrnt",
  "Redbubble",
  "Society6",
  "Big Cartel",
  "Instagram",
  "Linktree",
  "Other",
];

export const DEFAULT_LINK = () => ({
  label: "",
  platform: "",
  category: "apparel",
  product_type: "",
  purchase_url: "",
  price: "",
  currency: "USD",
  sizes: [],
  colors: [],
  note: "",
  is_primary: false,
  expanded: false,
});

/**
 * Helper you can reuse in pages:
 * links(editor) -> { merchLinks(payload), merchErrors(validation) }
 *
 * IMPORTANT:
 * This matches your Neon DB columns exactly:
 * product_type, purchase_url, label, platform, category, type,
 * price, currency, sizes(text[]), colors(text[]), note, is_primary
 *
 * FIX:
 * - DO NOT drop rows just because product_type is blank.
 * - Only require purchase_url for saving (so multiple links persist).
 * - Auto-fill product_type to keep DB clean (uses product_type > label > platform > "Merch").
 */
export function merchBuild(links) {
  // payload
  const merchLinks = (links || [])
    .map((l) => {
      const label = clampStr(l.label, 40).trim();
      const platform = clampStr(l.platform, 40).trim();
      const category = String(l.category || "other").trim();

      const url = String(l.purchase_url || "").trim();

      // allow empty product_type; auto-fill so the row isn't dropped
      const product_type =
        clampStr(l.product_type, 80).trim() ||
        label ||
        platform ||
        "Merch";

      const money = cleanMoney(l.price);
      const price = money && money !== "INVALID" ? Number(money) : null;

      const sizes = uniq(l.sizes);
      const colors = uniq(l.colors);

      const note = l.note ? String(l.note).slice(0, 300).trim() : "";

      // keep `type` as a mirror of product_type (optional)
      const type = product_type || "";

      return {
        product_type,
        purchase_url: url,
        label: label || null,
        platform: platform || null,
        category: category || null,
        type: type || null,
        price: Number.isFinite(price) ? price : null,
        currency: String(l.currency || "USD").trim() || "USD",
        sizes: sizes.length ? sizes : null,
        colors: colors.length ? colors : null,
        note: note || null,
        is_primary: !!l.is_primary,
      };
    })
    // Only require URL now (this is the core "only first link saved" fix)
    .filter((x) => x.purchase_url);

  // OPTIONAL: prevent TRUE duplicates only (same product_type + same URL)
  // Allows same URL for different product types (your goal).
  const seen = new Set();
  const deDupedTrue = [];
  for (const x of merchLinks) {
    const key = `${String(x.product_type).toLowerCase()}::${String(
      x.purchase_url
    ).toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deDupedTrue.push(x);
  }

  // validation (UX-level strictness; saving-level requires URL)
  const merchErrors = [];
  (links || []).forEach((l, idx) => {
    const hasAny =
      String(l.label || "").trim() ||
      String(l.platform || "").trim() ||
      String(l.product_type || "").trim() ||
      String(l.purchase_url || "").trim() ||
      String(l.price || "").trim() ||
      (l.sizes || []).length ||
      (l.colors || []).length;

    if (!hasAny) return;

    const url = String(l.purchase_url || "").trim();
    const money = cleanMoney(l.price);

    // product_type no longer required (we auto-fill it)
    if (!url) merchErrors.push({ idx, msg: "Missing URL." });
    else if (!isValidHttpUrl(url))
      merchErrors.push({ idx, msg: "URL must start with http(s)." });

    if (money === "INVALID")
      merchErrors.push({ idx, msg: "Invalid price (use 12 or 12.50)." });
  });

  return { merchLinks: deDupedTrue, merchErrors };
}

/* ------------------------------ component ------------------------------ */
export default function MerchLinksEditor({
  links,
  setLinks,
  merchErrors = [],
  title = "Shop / Merch Links",
  subtitle = "Add links to where people can buy this piece. Set one as featured.",
  allowPreset = true,
  maxLinks = 5, // standard limit
}) {
  function setLink(i, patch) {
    setLinks((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  function addLink(preset) {
    setLinks((prev) => {
      if ((prev || []).length >= maxLinks) return prev;
      return [...prev, { ...DEFAULT_LINK(), ...(preset || {}) }];
    });
  }

  function removeLink(i) {
    setLinks((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [DEFAULT_LINK()];
    });
  }

  function moveLink(i, dir) {
    setLinks((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[i];
      next[i] = next[j];
      next[j] = tmp;
      return next;
    });
  }

  function setPrimary(i) {
    setLinks((prev) => prev.map((x, idx) => ({ ...x, is_primary: idx === i })));
  }

  function toggleExpanded(i) {
    setLinks((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], expanded: !next[i].expanded };
      return next;
    });
  }

  function addChipValue(i, field, value) {
    setLinks((prev) => {
      const next = [...prev];
      const arr = Array.isArray(next[i][field]) ? next[i][field] : [];
      next[i] = { ...next[i], [field]: uniq([...arr, value]) };
      return next;
    });
  }

  function removeChipValue(i, field, value) {
    setLinks((prev) => {
      const next = [...prev];
      const arr = Array.isArray(next[i][field]) ? next[i][field] : [];
      next[i] = { ...next[i], [field]: arr.filter((x) => x !== value) };
      return next;
    });
  }

  function addPresetBundle() {
    addLink({
      category: "apparel",
      product_type: "T-Shirt",
      label: "Shop",
      platform: "Printify",
      expanded: true,
    });
    addLink({
      category: "apparel",
      product_type: "Hoodie",
      label: "Shop",
      platform: "Printify",
      expanded: true,
    });
    addLink({
      category: "prints",
      product_type: "Art Print",
      label: "Shop",
      platform: "InPrnt",
      expanded: true,
    });
  }

  const errorLookup = useMemo(() => {
    const map = new Map();
    merchErrors.forEach((e) => {
      const prev = map.get(e.idx) || [];
      prev.push(e.msg);
      map.set(e.idx, prev);
    });
    return map;
  }, [merchErrors]);

  const atLimit = (links || []).length >= maxLinks;

  return (
    <div className="rounded-[22px] border border-white/12 bg-black/15 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-white/85">{title}</div>
          <div className="mt-1 text-xs text-white/55">{subtitle}</div>
          <div className="mt-2 text-[11px] text-white/45">
            Max {maxLinks} links.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => addLink(DEFAULT_LINK())}
            disabled={atLimit}
            className="h-10 px-4 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-sm font-semibold text-white/80 disabled:opacity-50"
          >
            + Add
          </button>

          {allowPreset ? (
            <button
              type="button"
              onClick={addPresetBundle}
              disabled={atLimit}
              className="h-10 px-4 rounded-full border border-sky-300/25 bg-sky-300/10 hover:bg-sky-300/15 transition text-sm font-semibold text-sky-200 disabled:opacity-50"
              title="Adds a clean starter bundle"
            >
              Preset
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        {(links || []).map((l, i) => {
          const rowErrors = errorLookup.get(i) || [];
          const category = l.category || "other";
          const typeSuggestions =
            PRODUCT_TYPE_SUGGESTIONS[category] || PRODUCT_TYPE_SUGGESTIONS.other;

          const summaryTitle =
            l.label || l.platform || l.product_type || `Link ${i + 1}`;
          const urlOk = l.purchase_url?.trim()
            ? isValidHttpUrl(l.purchase_url)
            : null;

          return (
            <div
              key={i}
              className="
                rounded-[22px] border border-white/10
                bg-white/[0.04] backdrop-blur-xl
                shadow-[0_14px_60px_rgba(0,0,0,0.35)]
                overflow-hidden
              "
            >
              {/* Summary */}
              <div className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] tracking-[0.32em] uppercase text-white/50">
                        Link {i + 1}
                      </span>

                      {l.is_primary ? <Badge tone="sky">Featured</Badge> : null}

                      {urlOk === true ? (
                        <Badge>URL OK</Badge>
                      ) : urlOk === false ? (
                        <Badge tone="danger">Bad URL</Badge>
                      ) : null}
                    </div>

                    <div className="mt-2 truncate text-sm font-semibold text-white/85">
                      {summaryTitle}
                    </div>

                    <div className="mt-1 text-xs text-white/45 truncate">
                      {l.purchase_url?.trim() || "No URL yet"}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveLink(i, -1)}
                      className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-white/70 flex items-center justify-center"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLink(i, 1)}
                      className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-white/70 flex items-center justify-center"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => setPrimary(i)}
                      className="h-9 px-3 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-xs font-semibold text-white/70"
                      title="Set as featured"
                    >
                      ☆
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(i)}
                      className="h-9 px-3 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-xs font-semibold text-white/70"
                    >
                      {l.expanded ? "Hide" : "Edit"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLink(i)}
                      className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.04] hover:bg-red-400/10 hover:border-red-400/25 transition text-white/70 flex items-center justify-center"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {rowErrors.length ? (
                  <div className="mt-3 rounded-2xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-xs text-red-200">
                    {rowErrors.join(" ")}
                  </div>
                ) : null}
              </div>

              {/* Expanded */}
              {l.expanded ? (
                <div className="border-t border-white/10 px-4 py-5">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-4">
                      <div className="text-xs font-semibold text-white/70">
                        Platform
                      </div>
                      <input
                        value={l.platform}
                        onChange={(e) =>
                          setLink(i, { platform: clampStr(e.target.value, 40) })
                        }
                        placeholder="Printify, Etsy…"
                        className="mt-2"
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        {PLATFORM_SUGGESTIONS.slice(0, 5).map((p) => (
                          <MiniPill
                            key={p}
                            active={l.platform === p}
                            onClick={() => setLink(i, { platform: p })}
                          >
                            {p}
                          </MiniPill>
                        ))}
                      </div>
                    </div>

                    <div className="sm:col-span-4">
                      <div className="text-xs font-semibold text-white/70">
                        Category
                      </div>
                      <select
                        value={l.category}
                        onChange={(e) =>
                          setLink(i, { category: e.target.value })
                        }
                        className="mt-2"
                      >
                        {CATEGORY_PRESETS.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-4">
                      <div className="text-xs font-semibold text-white/70">
                        Button label
                      </div>
                      <input
                        value={l.label}
                        onChange={(e) =>
                          setLink(i, { label: clampStr(e.target.value, 40) })
                        }
                        placeholder="Shop / Buy / Commissions"
                        className="mt-2"
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <div className="text-xs font-semibold text-white/70">
                        Product type
                      </div>
                      <input
                        value={l.product_type}
                        onChange={(e) =>
                          setLink(i, {
                            product_type: clampStr(e.target.value, 80),
                          })
                        }
                        placeholder="Hoodie, Poster…"
                        className="mt-2"
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        {typeSuggestions.slice(0, 6).map((t) => (
                          <MiniPill
                            key={t}
                            active={l.product_type === t}
                            onClick={() => setLink(i, { product_type: t })}
                          >
                            {t}
                          </MiniPill>
                        ))}
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <div className="text-xs font-semibold text-white/70">
                        Purchase URL
                      </div>
                      <input
                        value={l.purchase_url}
                        onChange={(e) =>
                          setLink(i, { purchase_url: e.target.value })
                        }
                        placeholder="https://…"
                        className="mt-2"
                      />
                      <div className="mt-2 flex items-center justify-between text-xs text-white/45">
                        <span>
                          {l.purchase_url?.trim()
                            ? isValidHttpUrl(l.purchase_url)
                              ? "Looks good."
                              : "Must start with http(s)."
                            : "Required if you fill this row."}
                        </span>
                        {l.purchase_url?.trim() &&
                        isValidHttpUrl(l.purchase_url) ? (
                          <a
                            href={l.purchase_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sky-200/90 hover:text-sky-200 transition font-semibold"
                          >
                            Test →
                          </a>
                        ) : null}
                      </div>
                    </div>

                    <div className="sm:col-span-4">
                      <div className="text-xs font-semibold text-white/70">
                        Price (optional)
                      </div>
                      <input
                        value={l.price}
                        onChange={(e) =>
                          setLink(i, { price: clampStr(e.target.value, 10) })
                        }
                        placeholder="29.99"
                        className="mt-2"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-xs font-semibold text-white/70">
                        Currency
                      </div>
                      <select
                        value={l.currency}
                        onChange={(e) =>
                          setLink(i, { currency: e.target.value })
                        }
                        className="mt-2"
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-6">
                      <div className="text-xs font-semibold text-white/70">
                        Sizes (optional)
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {["XS", "S", "M", "L", "XL", "2XL"].map((s) => {
                          const active = (l.sizes || []).includes(s);
                          return (
                            <MiniPill
                              key={s}
                              active={active}
                              onClick={() =>
                                active
                                  ? removeChipValue(i, "sizes", s)
                                  : addChipValue(i, "sizes", s)
                              }
                            >
                              {s}
                            </MiniPill>
                          );
                        })}
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <div className="text-xs font-semibold text-white/70">
                        Colors (optional)
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {["Black", "White", "Cream", "Gray", "Navy"].map((c) => {
                          const active = (l.colors || []).includes(c);
                          return (
                            <MiniPill
                              key={c}
                              active={active}
                              onClick={() =>
                                active
                                  ? removeChipValue(i, "colors", c)
                                  : addChipValue(i, "colors", c)
                              }
                            >
                              {c}
                            </MiniPill>
                          );
                        })}
                      </div>
                    </div>

                    <div className="sm:col-span-12">
                      <div className="text-xs font-semibold text-white/70">
                        Note (optional)
                      </div>
                      <input
                        value={l.note}
                        onChange={(e) =>
                          setLink(i, { note: clampStr(e.target.value, 300) })
                        }
                        placeholder="Limited drop • Signed prints • Ships in 2–3 weeks"
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-xs text-white/45">
        Tip: keep it to 1–3 links. Set one “Featured”. (Max {maxLinks}.)
      </div>
    </div>
  );
}
